import { useState } from "react";
import { useBetSlip } from "../context/BetSlipContext";
import { Zap, Plus, ChevronDown, Check } from "lucide-react";

/**
 * Live-match add-to-slip widget.
 *
 * Default view: a single "⚡ Add {pick}" button where `pick` is the
 * model's recommended side based on momentum + score.
 *
 * Expanded view (click chevron): three buttons — Home / Draw / Away —
 * so the user can pick the side manually.  Selected picks go into the
 * global BetSlip with `is_live: true` so the backend can auto-split
 * the parlay (Betway regulates in-play and pre-match into separate
 * parlays).
 *
 * Props:
 *   match: object from `/api/live/now-playing` — must have
 *     fixture_id, home_team, away_team, league, score, momentum_leader.
 */
export const LiveBetButtons = ({ match }) => {
  const { addPick, isPicked } = useBetSlip();
  const [expanded, setExpanded] = useState(false);

  // Recommended side — momentum leader > score leader > home
  const recommendedSide =
    match?.momentum_leader === "home"
      ? "Home"
      : match?.momentum_leader === "away"
      ? "Away"
      : (match?.score?.home || 0) > (match?.score?.away || 0)
      ? "Home"
      : (match?.score?.away || 0) > (match?.score?.home || 0)
      ? "Away"
      : "Home";

  const common = {
    fixture_id: match.fixture_id,
    home_team: match.home_team?.name,
    away_team: match.away_team?.name,
    league: match.league?.name,
    country: match.league?.country,
    kickoff: null,                 // already kicked off
    market_type: "Match Result (Live)",
    is_live: true,
  };

  const makePick = (side) => {
    addPick({
      ...common,
      prediction: side,
      confidence: 50,               // placeholder — no confidence for live picks
      risk_level: "Live",
    });
  };

  const sides = [
    { side: "Home", label: match.home_team?.name || "Home" },
    { side: "Draw", label: "Draw" },
    { side: "Away", label: match.away_team?.name || "Away" },
  ];

  const picked = (side) =>
    isPicked(match.fixture_id, common.market_type, side);

  return (
    <div
      className="mt-3 space-y-2"
      data-testid={`live-bet-buttons-${match.fixture_id}`}
      onClick={(e) => e.stopPropagation()}
    >
      {!expanded && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => makePick(recommendedSide)}
            disabled={picked(recommendedSide)}
            data-testid={`live-add-default-${match.fixture_id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-destructive/10 hover:bg-destructive/20 border border-destructive/30 text-xs font-semibold text-destructive transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            title={`Model pick based on ${match?.momentum_leader !== 'tied' ? 'momentum' : 'current state'}`}
          >
            {picked(recommendedSide) ? (
              <>
                <Check className="w-3.5 h-3.5" />
                In slip ({recommendedSide})
              </>
            ) : (
              <>
                <Zap className="w-3.5 h-3.5" />
                Bet Live · {recommendedSide}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            data-testid={`live-expand-${match.fixture_id}`}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-muted hover:bg-muted-foreground/20 text-muted-foreground hover:text-foreground transition-colors"
            title="Pick a different side"
            aria-label="Expand live picks"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      )}

      {expanded && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground text-center">
            Pick a side · live 1X2
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {sides.map((s) => (
              <button
                key={s.side}
                type="button"
                onClick={() => makePick(s.side)}
                disabled={picked(s.side)}
                data-testid={`live-add-${s.side.toLowerCase()}-${match.fixture_id}`}
                className={`inline-flex items-center justify-center h-9 rounded-full px-2 text-[11px] font-semibold transition-colors truncate ${
                  picked(s.side)
                    ? "bg-destructive/20 text-destructive border border-destructive cursor-not-allowed"
                    : "bg-destructive/10 hover:bg-destructive/20 border border-destructive/30 text-destructive"
                }`}
              >
                {picked(s.side) ? <Check className="w-3 h-3 mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                <span className="truncate">{s.label}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="w-full text-[10px] text-muted-foreground hover:text-foreground transition-colors"
          >
            Collapse
          </button>
        </div>
      )}
    </div>
  );
};

export default LiveBetButtons;
